import { Box, Button, Center, Group, Image, Loader, Modal, Text, Tooltip } from '@mantine/core';
import { IconDownload, IconEye } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppNotification } from '../hooks/useAppNotification';

const PREVIEWABLE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

const PDF_CONTENT_TYPE = 'application/pdf';

export function isPreviewableContentType(contentType: string | null | undefined) {
  return contentType === PDF_CONTENT_TYPE || PREVIEWABLE_IMAGE_TYPES.includes(contentType ?? '');
}

interface FilePreviewButtonProps {
  contentType: string;
  disabled?: boolean;
  onClick: () => void;
}

export function FilePreviewButton({ contentType, disabled, onClick }: FilePreviewButtonProps) {
  const { t } = useTranslation();
  const previewable = isPreviewableContentType(contentType);

  // A disabled button emits no mouse events, so the tooltip hangs off a wrapper instead.
  return (
    <Tooltip
      label={t('tooltip.previewUnavailable')}
      disabled={previewable}
      events={{ hover: true, focus: true, touch: true }}
      multiline
      w={280}
    >
      <Box
        component="span"
        display="inline-block"
        data-disabled={!previewable || undefined}
      >
        <Button
          size="xs"
          variant="outline"
          leftSection={<IconEye size={14} />}
          disabled={disabled || !previewable}
          onClick={onClick}
        >
          {t('button.view')}
        </Button>
      </Box>
    </Tooltip>
  );
}

interface FilePreviewModalProps {
  title: string;
  contentType: string;
  fetchBlob: () => Promise<Blob>;
  onClose: () => void;
  onDownload: () => void;
  downloading?: boolean;
}

export function FilePreviewModal({
  title,
  contentType,
  fetchBlob,
  onClose,
  onDownload,
  downloading,
}: FilePreviewModalProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const [url, setUrl] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  // Callers pass fresh closures every render, and refetching the file on each would loop.
  const callbacks = useRef({ fetchBlob, onClose, notification });
  callbacks.current = { fetchBlob, onClose, notification };

  // Downloads arrive as application/octet-stream, so the viewer needs the type the file was stored with.
  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    callbacks.current
      .fetchBlob()
      .then(data => {
        if (!active) return;
        objectUrl = URL.createObjectURL(new Blob([data], { type: contentType }));
        setUrl(objectUrl);
      })
      .catch((error: Error) => {
        if (!active) return;
        callbacks.current.notification.error(error);
        callbacks.current.onClose();
      });

    return () => {
      active = false;
      if (objectUrl != null) URL.revokeObjectURL(objectUrl);
    };
  }, [contentType]);

  const renderContent = () => {
    if (url == null) {
      return (
        <Center h={200}>
          <Loader
            role="status"
            aria-label={t('label.previewLoading')}
          />
        </Center>
      );
    }
    if (contentType === PDF_CONTENT_TYPE) {
      return (
        <iframe
          src={url}
          title={title}
          style={{ width: '100%', height: '75vh', border: 0 }}
        />
      );
    }
    if (imageFailed) {
      return (
        <Center h={200}>
          <Text c="dimmed">{t('label.previewFailed')}</Text>
        </Center>
      );
    }
    return (
      <Image
        src={url}
        alt={title}
        fit="contain"
        mah="75vh"
        onError={() => setImageFailed(true)}
      />
    );
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={title}
      size="xl"
    >
      {renderContent()}
      <Group
        justify="flex-end"
        mt="md"
      >
        <Button
          leftSection={<IconDownload size={14} />}
          onClick={onDownload}
          loading={downloading}
        >
          {t('button.download')}
        </Button>
      </Group>
    </Modal>
  );
}
